import { isEmpty, pick, values, map, assign, keys } from 'lodash';
import qs from 'query-string';

import config from '../../../config';

const { queryParamsMap, referralParamsMap, searchPartners } = config;

export const mapParamsByConfig = (referralObject = {}, configMap = {}) => {
    const converted = {};
    const updatedReferObj = checkAndRemoveExcessReferralParams(referralObject);
    map(updatedReferObj, (val, key) => {
        converted[configMap[key]] = val;
    });

    return converted;
};

const findSearchPartner = (referrer) => {
    let organicReferral = {};
    map(searchPartners, (partner) => {
        if ( referrer.indexOf(partner) >= 0 ) {
            assign(organicReferral, {
                partnerReferral: partner,
                campaignReferral: 'organic'
            });
        }
    });
    if (isEmpty(organicReferral)) organicReferral = undefined;
    return organicReferral;
};

const parseProspectData = (storage) => {
    let parsedProspectData = {};
    try {
        parsedProspectData = JSON.parse(storage.getItem('prospect')) || {};
    } catch (e) {
        console.log(
            'components::routing::helpers::Failed to parse prospect from storage'
        );
    }
    return parsedProspectData;
};

/**
 * Parses a search query string and returns config-mapped output for those keys
 * if any params are available (can return { undefined: 'some value'}),
 * otherwise fall back to localStorage 'prospect' data (which should hold the
 * query parameters from the homepage.
 *
 * @param {string} queryString search param for a parsable string with P&C's ('?p=..&c=..')
 * @param {Pick<Storage, 'getItem'>} storage - e.g.: window.localStorage
 */
export const parseQueryString = (queryString, storage) => {
    // Treat this as "query string params from the home page";
    const parsedProspectData = parseProspectData(storage);
    let mappedOutput = {};

    const parsedUrlData = qs.parse(queryString);

    if (!isEmpty(parsedUrlData)) {
        mappedOutput = mapParamsByConfig(parsedUrlData, queryParamsMap);
    } else if (!isEmpty(parsedProspectData)) {
        mappedOutput = mapParamsByConfig(parsedProspectData, referralParamsMap);
    }

    return mappedOutput;
};

/**
 * Takes the query string and stores referral params in local storage in the prospect key if they exist.
 * This helps capture the original params a client may have used to get to the site.
 * @param {*} queryString raw query string, as from `location.search`
 * @param {*} storage window.localStorage
 */
export const captureReferralDataFromQueryParams = (queryString, storage) => {
    const parsedUrlData = qs.parse(queryString);
    const parsedProspectData = parseProspectData(storage);
    if (isEmpty(parsedProspectData) && !isEmpty(parsedUrlData)) {
        const referralParams = pick(parsedUrlData, keys(referralParamsMap));
        if (!isEmpty(referralParams)) {
            storage.setItem('prospect', JSON.stringify(referralParams));
        }
    }
};

/**
 * Gets full referral information from first parsed query params, falling  back
 * to organic referral based on document referrer, then to local storage
 * referrer, with a default of 'direct' campaign and partner if no other
 * sources are found.
 *
 * @param {string} queryString search param for a parsable string with P&C's ('?p=..&c=..')
 * @param {string} e.g.: `document.referrer`
 * @param {Pick<Storage, 'getItem'>} storage - e.g.: `window.localStorage`
 */
export const getReferralData = (queryParams, documentReferrer, storage) => {
    const explicitReferral = pick(queryParams, values(referralParamsMap));
    if (!isEmpty(explicitReferral)) {
        return explicitReferral;
    } else {
        const docReferrer = findSearchPartner(documentReferrer || '');
        const lsReferrer = findSearchPartner(storage.getItem('referrer') || '');
        const implicitReferral = docReferrer || lsReferrer || {
            campaignReferral: 'direct',
            partnerReferral: 'direct'
        };
        return implicitReferral;
    }
};

/**
 * Prevents bug when more than one param code ex: ?p=123&c=abc&p=456.
 * It strips the second code and it's value.
 * Bug ticket AP-5355
 *
 * @param {object} referObj referrer object
 * @returns {object} returns original referral object or the referral object with duplicate campaign codes stripped.
 */
const checkAndRemoveExcessReferralParams = (referObj) => {
    if (!referObj) return referObj;

    const referObjKeys = Object.keys(referObj);
    referObjKeys.forEach((key) => {
        if (Array.isArray(referObj[key])) {
            referObj[key] = referObj[key][0];
        }
    });
    return referObj;
};


/**
 * Extracts query parameters from a URL string
 * 
 * @function extractQueryParams
 * @param {string} urlString - The full URL or hash string to extract parameters from
 * @param {string[]} [includedParams] - Optional array of parameter names to extract. All parameters are extracted if none specified.
 * @returns {string} Reconstructed query string with extracted parameters if any.
 */
export const extractQueryParams = (urlString, includedParams) => {
    if (!urlString) return ''

    const [, queryPart] = urlString.split('?');

    if (!includedParams || includedParams.length === 0) {
        return queryPart ? `?${queryPart}` : '';
    }

    const searchParams = new URLSearchParams(queryPart || '');

    const extractedParams = includedParams
        .filter(param => searchParams.has(param))
        .map(param => {
            const value = searchParams.get(param);
            return `${encodeURIComponent(param)}=${encodeURIComponent(value)}`;
        })
        .join('&');

        return extractedParams ? `?${extractedParams}` : '';
};

/**
 * Routes to a specified base route while preserving any specified query parameters from the current URL hash.
 * 
 * @param {string} baseRoute - The target route to navigate to
 * @param {string[]} [includedParams] - Optional array of parameter names to include. All parameters are included if none specified.
 */
export const routeWithParams = (router, baseRoute, includedParams) => {
    const queryString = extractQueryParams(window.location.hash, includedParams)

    router.route(`${baseRoute}${queryString}`);
};